import React, { useState } from 'react';
import './ActivityFilters.css';

const ActivityFilters = ({ filters, onFilterChange, users = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    onFilterChange(newFilters);
  };

  const handleReset = () => {
    const resetFilters = {
      user_id: '',
      action_type: '',
      start_date: '',
      end_date: '',
      search: ''
    };
    onFilterChange(resetFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const actionTypes = [
    { value: 'create', label: '✨ Création' },
    { value: 'update', label: '✏️ Modification' },
    { value: 'delete', label: '🗑️ Suppression' },
    { value: 'send', label: '📤 Envoi' },
    { value: 'relaunch', label: '🔁 Relance' },
    { value: 'comment', label: '💬 Commentaire' },
    { value: 'sign', label: '✅ Signature' },
    { value: 'assign', label: '👤 Assignation' },
    { value: 'status_change', label: '🔄 Changement de statut' },
    { value: 'archive', label: '📁 Archivage' },
    { value: 'restore', label: '🔄 Restauration' },
    // Phase 2 - Nouvelles actions
    { value: 'tag', label: '🏷️ Mention utilisateur' },
    { value: 'unassign', label: '👤❌ Désassignation' },
    { value: 'notification', label: '🔔 Notification' }
  ];

  const quickFilters = [
    { 
      label: 'Aujourd\'hui', 
      action: () => {
        const today = new Date().toISOString().split('T')[0];
        handleFilterChange('start_date', today);
        handleFilterChange('end_date', today);
      }
    },
    { 
      label: 'Cette semaine', 
      action: () => {
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        handleFilterChange('start_date', oneWeekAgo.toISOString().split('T')[0]);
        handleFilterChange('end_date', now.toISOString().split('T')[0]);
      }
    },
    { 
      label: 'Ce mois', 
      action: () => {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        handleFilterChange('start_date', firstDayOfMonth.toISOString().split('T')[0]);
        handleFilterChange('end_date', now.toISOString().split('T')[0]);
      }
    }
  ];

  return (
    <div className="activity-filters">
      <div className="filters-header">
        <div className="filters-title">
          <span className="filter-icon">🔍</span>
          <h3>Filtres</h3>
          {hasActiveFilters && (
            <span className="active-filters-count">
              {Object.values(filters).filter(v => v !== '').length} actif{Object.values(filters).filter(v => v !== '').length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="filters-actions">
          {hasActiveFilters && (
            <button onClick={handleReset} className="reset-filters-btn">
              ↻ Réinitialiser
            </button>
          )}
          <button 
            onClick={() => setIsExpanded(!isExpanded)} 
            className="expand-filters-btn"
          >
            {isExpanded ? '🔽 Masquer' : '🔼 Afficher'}
          </button>
        </div>
      </div>

      {/* Filtres rapides toujours visibles */}
      <div className="quick-filters">
        {quickFilters.map((filter, index) => (
          <button
            key={index}
            onClick={filter.action}
            className="quick-filter-btn"
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Filtres détaillés */}
      {isExpanded && (
        <div className="detailed-filters">
          <div className="filters-grid">
            {/* Recherche textuelle */}
            <div className="filter-group">
              <label htmlFor="search">🔍 Recherche</label>
              <input
                id="search"
                type="text"
                placeholder="Rechercher dans l'historique..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="filter-input"
              />
            </div>

            {/* Filtre par utilisateur */}
            <div className="filter-group">
              <label htmlFor="user_id">👤 Utilisateur</label>
              <select
                id="user_id"
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="filter-select"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par type d'action */}
            <div className="filter-group">
              <label htmlFor="action_type">⚡ Type d'action</label>
              <select
                id="action_type"
                value={filters.action_type}
                onChange={(e) => handleFilterChange('action_type', e.target.value)}
                className="filter-select"
              >
                <option value="">Toutes les actions</option>
                {actionTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par date de début */}
            <div className="filter-group">
              <label htmlFor="start_date">📅 Date de début</label>
              <input
                id="start_date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="filter-input"
              />
            </div>

            {/* Filtre par date de fin */}
            <div className="filter-group">
              <label htmlFor="end_date">📅 Date de fin</label>
              <input
                id="end_date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="filter-input"
              />
            </div>
          </div>

          {/* Résumé des filtres actifs */}
          {hasActiveFilters && (
            <div className="active-filters-summary">
              <h4>Filtres actifs :</h4>
              <div className="active-filters-list">
                {filters.search && (
                  <div className="active-filter">
                    <span className="filter-label">Recherche:</span>
                    <span className="filter-value">"{filters.search}"</span>
                    <button 
                      onClick={() => handleFilterChange('search', '')}
                      className="remove-filter"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {filters.user_id && (
                  <div className="active-filter">
                    <span className="filter-label">Utilisateur:</span>
                    <span className="filter-value">
                      {users.find(u => u.id === filters.user_id)?.name || filters.user_id}
                    </span>
                    <button 
                      onClick={() => handleFilterChange('user_id', '')}
                      className="remove-filter"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {filters.action_type && (
                  <div className="active-filter">
                    <span className="filter-label">Action:</span>
                    <span className="filter-value">
                      {actionTypes.find(t => t.value === filters.action_type)?.label || filters.action_type}
                    </span>
                    <button 
                      onClick={() => handleFilterChange('action_type', '')}
                      className="remove-filter"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {filters.start_date && (
                  <div className="active-filter">
                    <span className="filter-label">Depuis:</span>
                    <span className="filter-value">
                      {new Date(filters.start_date).toLocaleDateString('fr-FR')}
                    </span>
                    <button 
                      onClick={() => handleFilterChange('start_date', '')}
                      className="remove-filter"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {filters.end_date && (
                  <div className="active-filter">
                    <span className="filter-label">Jusqu'à:</span>
                    <span className="filter-value">
                      {new Date(filters.end_date).toLocaleDateString('fr-FR')}
                    </span>
                    <button 
                      onClick={() => handleFilterChange('end_date', '')}
                      className="remove-filter"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActivityFilters;